import React, { useState, useEffect, useCallback } from 'react';
import {
  UsersIcon,
  UserGroupIcon,
  CreditCardIcon,
  AcademicCapIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  EyeIcon,
  PlusIcon,
  ChartBarIcon,
  CheckCircleIcon,
  StarIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import apiService from '../services/api';
import { useDataCache } from '../contexts/DataCacheContext';

const Overview = () => {
  const { students: cachedStudents, courses: cachedCourses, getStudents: cacheGetStudents, getCourses: cacheGetCourses } = useDataCache();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalStudents: 0,
    activeBatches: 0,
    pendingPayments: 0,
    totalTeachers: 0
  });
  const [recentStudents, setRecentStudents] = useState([]);
  const [pendingPaymentStudents, setPendingPaymentStudents] = useState([]);
  const [batchPerformance, setBatchPerformance] = useState([]);
  const [courses, setCourses] = useState([]);

  // Helper function to determine batch status based on dates (same as BatchManagement)
  const getBatchStatus = useCallback((startDate, endDate) => {
    const today = new Date();
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    today.setHours(0, 0, 0, 0);
    start.setHours(0, 0, 0, 0);
    end.setHours(0, 0, 0, 0);
    
    if (today < start) return 'Upcoming';
    if (today > end) return 'Completed';
    return 'Active';
  }, []);

  const loadDashboardData = useCallback(async () => {
    try {
      setLoading(true);
      const [studentsData, teachersData, batchesData, pendingData, coursesData] = await Promise.all([
        cacheGetStudents({ background: false }).catch(err => { console.error('Failed to load students:', err); return []; }),
        apiService.getTeachers().catch(err => { console.error('Failed to load teachers:', err); return []; }),
        apiService.getBatches().catch(err => { console.error('Failed to load batches:', err); return []; }),
        apiService.getPendingPayments().catch(err => { console.error('Failed to load pending payments:', err); return []; }),
        cacheGetCourses({ background: false }).catch(err => { console.error('Failed to load courses:', err); return []; })
      ]);

      // Calculate batch statuses based on dates
      const processedBatches = Array.isArray(batchesData) ? batchesData.map(batch => ({
        ...batch,
        calculatedStatus: getBatchStatus(batch.start_date, batch.end_date)
      })) : [];

      // Calculate stats
      setStats({
        totalStudents: Array.isArray(studentsData) ? studentsData.length : 0,
        activeBatches: processedBatches.filter(batch => batch.calculatedStatus === 'Active').length,
        pendingPayments: Array.isArray(pendingData) ? pendingData.length : 0,
        totalTeachers: Array.isArray(teachersData) ? teachersData.length : 0
      });

      // Set recent students (last 5)
      if (Array.isArray(studentsData)) {
        setRecentStudents(studentsData.slice(0, 5));
      }

      setCourses(Array.isArray(coursesData) ? coursesData : []);
      
      // Set pending payment students from API data
      if (Array.isArray(pendingData) && pendingData.length > 0) {
        const formattedPendingPayments = pendingData.slice(0, 4).map((student) => ({
          name: student.full_name || student.name || 'Unknown Student',
          course: student.course?.name || student.course || 'No Course Assigned',
          amount: student.amount || student.fee_amount || '₦0',
          dueDate: student.due_date || (student.created_at ? new Date(student.created_at).toLocaleDateString() : 'Unknown'),
          avatar: student.full_name ? student.full_name.split(' ').map(n => n[0]).join('').slice(0, 2) : 'ST'
        }));
        setPendingPaymentStudents(formattedPendingPayments);
      }
      
      // Set batch performance from actual batch data with calculated status
      if (processedBatches.length > 0) {
        const performanceData = processedBatches.map(batch => ({
          name: batch.name || 'Unnamed Batch',
          students: batch.students_count || batch.students?.length || 0,
          completion: batch.completion_percentage || 0,
          status: batch.calculatedStatus === 'Active' ? 'On Track' : batch.calculatedStatus === 'Completed' ? 'Excellent' : 'Needs Attention'
        }));
        setBatchPerformance(performanceData.slice(0, 4));
      }

    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  }, [getBatchStatus]);

  const getStudentCourseName = useCallback((student) => {
    if (!student) return 'No Course Assigned';

    if (student.course_detail?.name) return student.course_detail.name;
    if (student.course?.name) return student.course.name;
    if (student.course_name) return student.course_name;
    if (student.course_title) return student.course_title;

    const courseId = student.course;
    if (courseId && Array.isArray(courses) && courses.length > 0) {
      const courseMatch = courses.find(course => course.id === courseId || course.id === String(courseId) || course.pk === courseId);
      if (courseMatch?.name) {
        return courseMatch.name;
      }
    }

    return 'No Course Assigned';
  }, [courses]);

  useEffect(() => {
    // Seed with cache instantly to avoid spinners
    if (Array.isArray(cachedStudents) && cachedStudents.length > 0) {
      setRecentStudents(cachedStudents.slice(0, 5));
    }
    if (Array.isArray(cachedCourses) && cachedCourses.length > 0) {
      setCourses(cachedCourses);
    }
    loadDashboardData();
  }, [loadDashboardData]);

  const dashboardStats = [
    {
      name: 'Total Students',
      value: loading ? '...' : stats.totalStudents.toString(),
      change: '+12%',
      changeType: 'increase',
      icon: UsersIcon,
      iconBg: 'bg-white/20',
      cardBg: 'bg-red-900',
      textColor: 'text-white'
    },
    {
      name: 'Active Batches',
      value: loading ? '...' : stats.activeBatches.toString(),
      change: '+2',
      changeType: 'increase',
      icon: UserGroupIcon,
      iconBg: 'bg-white/20',
      cardBg: 'bg-gray-800',
      textColor: 'text-white'
    },
    {
      name: 'Pending Payments',
      value: loading ? '...' : stats.pendingPayments.toString(),
      change: '-5',
      changeType: 'decrease',
      icon: CreditCardIcon,
      iconBg: 'bg-white/20',
      cardBg: 'bg-gray-700',
      textColor: 'text-white'
    },
    {
      name: 'Teachers',
      value: loading ? '...' : stats.totalTeachers.toString(),
      change: '+3',
      changeType: 'increase',
      icon: AcademicCapIcon,
      iconBg: 'bg-white/20',
      cardBg: 'bg-blue-900',
      textColor: 'text-white'
    }
  ];


  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-primary-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold mb-2">Welcome back, Admin! 👋</h1>
            <p className="text-primary-100">Here's what's happening with your Aitech platform today.</p>
          </div>
          <div className="hidden md:block">
            <div className="text-right">
              <p className="text-sm text-primary-100">Today</p>
              <p className="text-lg font-semibold">{new Date().toLocaleDateString('en-US', { 
                weekday: 'long', 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardStats.map((stat, index) => (
          <div key={index} className={`${stat.cardBg} rounded-xl p-6 shadow-lg animate-fade-in`} style={{ animationDelay: `${index * 100}ms` }}>
            <div className="flex items-center">
              <div className={`p-3 rounded-lg ${stat.iconBg}`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <div className="ml-4 flex-1">
                <p className={`text-sm font-medium ${stat.textColor} opacity-90`}>{stat.name}</p>
                <div className="flex items-center">
                  <p className={`text-2xl font-bold ${stat.textColor}`}>{stat.value}</p>
                  <div className={`ml-2 flex items-center text-sm ${
                    stat.changeType === 'increase' ? 'text-green-300' : 'text-red-300'
                  }`}>
                    {stat.changeType === 'increase' ? (
                      <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                    ) : (
                      <ArrowTrendingDownIcon className="h-4 w-4 mr-1" />
                    )}
                    {stat.change}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="flex flex-col lg:flex-row gap-6">
        {/* Recent Students */}
        <div className="flex-1">
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Recent Student Registrations</h3>
              <button className="btn-secondary text-sm">
                <EyeIcon className="h-4 w-4 mr-2" />
                View All
              </button>
            </div>
            
            <div className="overflow-hidden">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 text-sm font-medium text-gray-500">Student</th>
                    <th className="text-left py-3 text-sm font-medium text-gray-500">Course</th>
                    <th className="text-left py-3 text-sm font-medium text-gray-500">Status</th>
                    <th className="text-left py-3 text-sm font-medium text-gray-500">Join Date</th>
                  </tr>
                </thead>
                    <tbody className="divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan="4" className="py-8 text-center text-gray-500">
                        Loading recent students...
                      </td>
                    </tr>
                  ) : recentStudents.length === 0 ? (
                    <tr>
                      <td colSpan="4" className="py-8 text-center text-gray-500">
                        No students registered yet.
                      </td>
                    </tr>
                  ) : (
                    recentStudents.map((student, index) => (
                      <tr
                        key={student.id || index}
                        className={`${index % 2 === 0 ? 'bg-gray-200' : 'bg-gray-100'} hover:bg-gray-300 transition-colors`}
                      >
                        <td className="py-3">
                          <div className="flex items-center">
                            <div className="w-8 h-8 bg-gradient-to-r from-primary-500 to-primary-600 rounded-full flex items-center justify-center mr-3">
                              <span className="text-white text-xs font-medium">
                                {student.full_name ? student.full_name.split(' ').map(n => n[0]).join('').slice(0, 2) : 'ST'}
                              </span>
                            </div>
                            <span className="font-medium text-gray-900">{student.full_name}</span>
                          </div>
                        </td>
                        <td className="py-3 text-sm text-gray-600">{getStudentCourseName(student)}</td>
                        <td className="py-3">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            student.payment_confirmed 
                              ? 'bg-green-100 text-green-800' 
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {student.payment_confirmed ? 'Paid' : 'Pending'}
                          </span>
                        </td>
                        <td className="py-3 text-sm text-gray-600">
                          {new Date(student.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="w-72 space-y-6 flex-shrink-0">
          <div className="bg-gray-800 rounded-xl p-4">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <h3 className="text-base font-bold text-white">Quick Actions</h3>
                <span className="bg-gray-700 text-white text-xs font-semibold px-2 py-0.5 rounded-full">4</span>
              </div>
              <button className="text-blue-400 text-xs hover:text-blue-300 flex items-center gap-1">
                Manage <ChevronRightIcon className="h-3 w-3" />
              </button>
            </div>
            <div className="space-y-2">
              <button className="w-full bg-gray-700 border border-orange-900/50 rounded-lg p-3 hover:bg-gray-600 transition-colors flex items-center gap-3 group">
                <div className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <PlusIcon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white">Register New Student</p>
                  <p className="text-xs text-blue-400 mt-0.5">Student Management</p>
                </div>
                <StarIcon className="h-4 w-4 text-gray-500 group-hover:text-yellow-400 transition-colors flex-shrink-0" />
              </button>
              
              <button className="w-full bg-gray-800 rounded-lg p-3 hover:bg-gray-700 transition-colors flex items-center gap-3 group">
                <div className="w-10 h-10 border-2 border-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <UserGroupIcon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white">Create New Batch</p>
                  <p className="text-xs text-blue-400 mt-0.5">Batch Management</p>
                </div>
                <StarIcon className="h-4 w-4 text-gray-500 group-hover:text-yellow-400 transition-colors flex-shrink-0" />
              </button>
              
              <button className="w-full bg-gray-800 rounded-lg p-3 hover:bg-gray-700 transition-colors flex items-center gap-3 group">
                <div className="w-10 h-10 border-2 border-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <CheckCircleIcon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white">Confirm Payments</p>
                  <p className="text-xs text-blue-400 mt-0.5">Payment Processing</p>
                </div>
                <StarIcon className="h-4 w-4 text-gray-500 group-hover:text-yellow-400 transition-colors flex-shrink-0" />
              </button>
              
              <button className="w-full bg-gray-800 rounded-lg p-3 hover:bg-gray-700 transition-colors flex items-center gap-3 group">
                <div className="w-10 h-10 border-2 border-blue-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <ChartBarIcon className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white">View Analytics</p>
                  <p className="text-xs text-blue-400 mt-0.5">Dashboard Insights</p>
                </div>
                <StarIcon className="h-4 w-4 text-gray-500 group-hover:text-yellow-400 transition-colors flex-shrink-0" />
              </button>
            </div>
          </div>

          {/* Batch Performance */}
          <div className="card">
            <h3 className="text-base font-semibold text-gray-900 mb-6">Batch Performance</h3>
            
            {loading ? (
              <div className="text-center py-8 text-xs text-gray-500">
                Loading batch performance...
              </div>
            ) : batchPerformance.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <div className="w-16 h-16 bg-gradient-to-br from-indigo-100 to-purple-100 rounded-2xl flex items-center justify-center mb-4 shadow-sm">
                  <UserGroupIcon className="h-8 w-8 text-indigo-500" />
                </div>
                <h4 className="text-base font-semibold text-gray-900 mb-2">No batches yet</h4>
                <p className="text-xs text-gray-500 text-center mb-6 max-w-xs">
                  Start tracking performance by creating your first batch. Monitor student progress and completion rates.
                </p>
                <button className="btn-primary text-xs">
                  <PlusIcon className="h-3.5 w-3.5 mr-2" />
                  Create New Batch
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {batchPerformance.map((batch, index) => (
                  <div key={index} className="border border-gray-200 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-sm font-medium text-gray-900">{batch.name}</h4>
                      <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${
                        batch.status === 'Excellent' ? 'bg-green-100 text-green-800' :
                        batch.status === 'On Track' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {batch.status}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-gray-600 mb-2">
                      <span>{batch.students} Students</span>
                      <span>{batch.completion}% Complete</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-2">
                      <div 
                        className={`h-2 rounded-full ${
                          batch.completion >= 90 ? 'bg-green-500' :
                          batch.completion >= 80 ? 'bg-blue-500' :
                          'bg-yellow-500'
                        }`}
                        style={{ width: `${batch.completion}%` }}
                      ></div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Pending Payments */}
      {pendingPaymentStudents.length > 0 && !loading && (
        <div className="grid grid-cols-1 gap-6">
          <div className="card">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-semibold text-gray-900">Pending Payments</h3>
              <span className="bg-red-100 text-red-800 text-xs font-semibold px-2 py-1 rounded-full">
                {pendingPaymentStudents.length} Pending
              </span>
            </div>
            
            <div className="space-y-4">
              {pendingPaymentStudents.map((payment, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center mr-3">
                      <span className="text-white text-sm font-medium">{payment.avatar}</span>
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{payment.name}</p>
                      <p className="text-sm text-gray-600">{payment.course}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-semibold text-gray-900">{payment.amount}</p>
                    <p className="text-xs text-red-600">Due: {payment.dueDate}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <button className="w-full mt-4 btn-primary">
              Process All Payments
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview; 